// Horizontal-scroll affordances for phones (docs/design/DESIGN_SYSTEM.md → "Mobile").
//
//   useHScrollFade(ref)  — keeps `data-overflow` ("left" | "right" | "both" |
//                          "none") on a `.pb-hscroll` element in sync with its
//                          scroll state so tokens.css can draw an edge fade only
//                          when something is actually hidden.
//   useSwipe(ref, opts)  — pointer-event swipe between tabs: 48 px threshold,
//                          horizontal-dominant, touch pointers only, no library.
//                          Ignores gestures that start inside a chart (scrubbing)
//                          or inside a horizontally scrollable row.

import { useEffect, type RefObject } from "react";

export type Overflow = "none" | "left" | "right" | "both";

export function overflowOf(el: HTMLElement): Overflow {
  const max = el.scrollWidth - el.clientWidth;
  if (max <= 1) return "none";
  const left = el.scrollLeft > 1;
  const right = el.scrollLeft < max - 1;
  return left && right ? "both" : left ? "left" : right ? "right" : "none";
}

/** `rearm`: any value whose change should re-attach the observers (e.g. the element
 *  only mounts once a phone→table switch happens). */
export function useHScrollFade<T extends HTMLElement>(ref: RefObject<T>, rearm?: unknown): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const next = overflowOf(el);
      if (el.dataset.overflow !== next) el.dataset.overflow = next;
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    el.addEventListener("scroll", schedule, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(el);
    // Children appearing / disappearing (tabs added, filters cleared).
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener("scroll", schedule);
      ro?.disconnect();
      mo?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ref, rearm]);
}

export interface SwipeOptions {
  /** Finger moved left → the next tab. */
  onLeft?: () => void;
  /** Finger moved right → the previous tab. */
  onRight?: () => void;
  /** Minimum horizontal travel in px (default 48). */
  threshold?: number;
  /** Also accept mouse / pen drags (default: touch only). */
  anyPointer?: boolean;
  disabled?: boolean;
}

const SCROLLER = ".recharts-wrapper, [data-chart-frame], .pb-hscroll, input, textarea, select, [role=slider]";

function insideHorizontalScroller(target: EventTarget | null, root: HTMLElement): boolean {
  let el = target instanceof HTMLElement ? target : null;
  while (el && el !== root) {
    if (el.matches(SCROLLER)) return true;
    const style = window.getComputedStyle(el);
    if ((style.overflowX === "auto" || style.overflowX === "scroll") && el.scrollWidth > el.clientWidth + 1) return true;
    el = el.parentElement;
  }
  return false;
}

export function useSwipe<T extends HTMLElement>(ref: RefObject<T>, { onLeft, onRight, threshold = 48, anyPointer = false, disabled }: SwipeOptions): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || disabled || (!onLeft && !onRight)) return;
    let start: { x: number; y: number; id: number } | null = null;
    const down = (e: PointerEvent) => {
      if (!anyPointer && e.pointerType !== "touch") return;
      if (insideHorizontalScroller(e.target, el)) return;
      start = { x: e.clientX, y: e.clientY, id: e.pointerId };
    };
    const up = (e: PointerEvent) => {
      if (!start || e.pointerId !== start.id) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      start = null;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0) onLeft?.();
      else onRight?.();
    };
    const cancel = () => {
      start = null;
    };
    el.addEventListener("pointerdown", down, { passive: true });
    el.addEventListener("pointerup", up, { passive: true });
    el.addEventListener("pointercancel", cancel, { passive: true });
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
    };
  }, [ref, onLeft, onRight, threshold, anyPointer, disabled]);
}
